import { useEffect, useState, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { DollarSign, FileText, Plus, Check, RefreshCw, Settings, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import RevenueDashboard from './RevenueDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METHOD_LABELS = { ach: 'ACH bank transfer', check: 'Mail-in check', wire: 'Wire transfer' };
const STATUS_COLORS = {
  draft: 'bg-zinc-700 text-zinc-200',
  sent: 'bg-blue-700 text-white',
  partial: 'bg-amber-600 text-white',
  paid: 'bg-green-700 text-white',
  overdue: 'bg-red-700 text-white',
  void: 'bg-zinc-600 text-zinc-200',
};

export default function BillingHub({ dataTestId = 'billing-hub' }) {
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [genMonth, setGenMonth] = useState('');
  const [recordOpen, setRecordOpen] = useState(false);
  const [activeInv, setActiveInv] = useState(null);
  const [recordForm, setRecordForm] = useState({ method: 'ach', amount: '', reference: '', note: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [invRes, contractRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/billing/invoices`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/contracts`, { credentials: 'include' }),
      ]);
      if (invRes.ok) setInvoices((await invRes.json()).invoices || []);
      if (contractRes.ok) {
        const cdata = await contractRes.json();
        setContracts(Array.isArray(cdata) ? cdata : (cdata.contracts || []));
      }
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const generateInvoices = async () => {
    try {
      const body = genMonth ? { month: genMonth } : {};
      const res = await fetch(`${BACKEND_URL}/api/billing/invoices/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Generated ${data.generated} invoice(s) for ${data.month}`);
      fetchAll();
    } catch (_e) { toast.error('Failed'); }
  };

  const runScheduler = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/billing/scheduler/run-now`, { method: 'POST', credentials: 'include' });
      toast.success('Scheduler executed (auto-generate + reminders)');
      fetchAll();
    } catch (_e) { toast.error('Failed'); }
  };

  const downloadPDF = async (inv) => {
    const res = await fetch(`${BACKEND_URL}/api/billing/invoices/${inv.invoice_id}/pdf`, { credentials: 'include' });
    if (!res.ok) { toast.error('Could not download'); return; }
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = u; a.download = `${inv.invoice_number}.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(u);
  };

  const openRecord = (inv) => {
    setActiveInv(inv);
    setRecordForm({ method: (inv.allowed_payment_methods?.[0]) || 'ach', amount: String(inv.balance || ''), reference: '', note: '' });
    setRecordOpen(true);
  };

  const submitPayment = async () => {
    if (!activeInv) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/invoices/${activeInv.invoice_id}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...recordForm, amount: parseFloat(recordForm.amount || '0') }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Recorded payment — status now ${data.status}`);
      setRecordOpen(false);
      fetchAll();
    } catch (_e) { toast.error('Failed'); }
  };

  const setMethods = async (contractId, methods) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/contracts/${contractId}/payment-methods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ allowed_methods: methods }),
      });
      if (res.ok) {
        toast.success('Updated allowed methods');
        fetchAll();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed');
      }
    } catch (_e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      <Tabs defaultValue="invoices">
        <TabsList className="bg-zinc-900">
          <TabsTrigger value="invoices" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black" data-testid="billing-subtab-invoices">
            <FileText className="h-4 w-4 mr-1" /> Invoices
          </TabsTrigger>
          <TabsTrigger value="revenue" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black" data-testid="billing-subtab-revenue">
            <TrendingUp className="h-4 w-4 mr-1" /> Revenue & A/R
          </TabsTrigger>
        </TabsList>

        <TabsContent value="revenue" className="space-y-4">
          <RevenueDashboard dataTestId="company-revenue-dashboard" />
        </TabsContent>

        <TabsContent value="invoices" className="space-y-4">
      <Card className="bg-zinc-900 border-zinc-700 p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6 text-[#C9A962]" />
          <div>
            <h3 className="text-white text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>BILLING & INVOICES</h3>
            <p className="text-xs text-zinc-400">Generate invoices, configure per-contract payment methods, record payments. Auto-cron runs on the 1st @ 09:00 UTC.</p>
          </div>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <Label className="text-xs text-zinc-400">Month (YYYY-MM)</Label>
            <Input value={genMonth} onChange={(e) => setGenMonth(e.target.value)} placeholder="2026-03" className="bg-zinc-800 border-zinc-700 text-white w-[140px]" data-testid="billing-gen-month" />
          </div>
          <Button onClick={generateInvoices} className="font-bold" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} data-testid="billing-generate">
            <Plus className="h-4 w-4 mr-1" /> Generate
          </Button>
          <Button variant="outline" className="border-[#C9A962] text-[#C9A962]" onClick={runScheduler} data-testid="billing-run-scheduler">
            <RefreshCw className="h-4 w-4 mr-1" /> Run cron now
          </Button>
        </div>
      </Card>

      {/* Per-contract payment methods */}
      <Card className="bg-zinc-900 border-zinc-700 p-5">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><Settings className="h-4 w-4 text-[#C9A962]" /> Per-Contract Payment Methods</h4>
        {contracts.length === 0 ? (
          <p className="text-zinc-500 text-sm">No contracts yet.</p>
        ) : (
          <div className="space-y-2">
            {contracts.map((c) => (
              <ContractMethodsRow key={c.contract_id} contract={c} onSave={setMethods} />
            ))}
          </div>
        )}
      </Card>

      {/* Invoice list */}
      <Card className="bg-zinc-900 border-zinc-700 p-5">
        <h4 className="text-white font-semibold mb-3">Invoices ({invoices.length})</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-400 border-b border-white/10">
                <th className="py-2 pr-3">Invoice #</th>
                <th className="py-2 pr-3">Property</th>
                <th className="py-2 pr-3">Period</th>
                <th className="py-2 pr-3">Total</th>
                <th className="py-2 pr-3">Balance</th>
                <th className="py-2 pr-3">Due</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.invoice_id} className="border-b border-white/5" data-testid={`invoice-row-${inv.invoice_id}`}>
                  <td className="py-2 pr-3 text-[#C9A962] font-mono text-xs">{inv.invoice_number}</td>
                  <td className="py-2 pr-3 text-white">{inv.property_name}</td>
                  <td className="py-2 pr-3 text-zinc-300">{inv.period_label}</td>
                  <td className="py-2 pr-3 text-white">${inv.total?.toFixed(2)}</td>
                  <td className="py-2 pr-3 text-white">${inv.balance?.toFixed(2)}</td>
                  <td className="py-2 pr-3 text-zinc-400">{inv.due_date}</td>
                  <td className="py-2 pr-3">
                    <Badge className={STATUS_COLORS[inv.status] || 'bg-zinc-700'}>{(inv.status || '').toUpperCase()}</Badge>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="outline" className="border-zinc-600 text-zinc-300" onClick={() => downloadPDF(inv)} data-testid={`invoice-pdf-${inv.invoice_id}`}>
                        <FileText className="h-3.5 w-3.5" />
                      </Button>
                      {inv.status !== 'paid' && (
                        <Button size="sm" className="font-bold" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} onClick={() => openRecord(inv)} data-testid={`invoice-mark-paid-${inv.invoice_id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Record payment
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr><td colSpan={8} className="py-4 text-zinc-500 text-center">No invoices yet — click Generate.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Record payment dialog */}
      <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
        <DialogContent className="bg-[#2D2D2D] text-white border-[#C9A962] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#C9A962]">Record Payment — {activeInv?.invoice_number}</DialogTitle>
          </DialogHeader>
          {activeInv && (
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-300">Method</Label>
                <Select value={recordForm.method} onValueChange={(v) => setRecordForm({ ...recordForm, method: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="record-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(activeInv.allowed_payment_methods || ['ach', 'check', 'wire']).map((m) => (
                      <SelectItem key={m} value={m}>{METHOD_LABELS[m]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Amount received ($)</Label>
                <Input type="number" step="0.01" value={recordForm.amount} onChange={(e) => setRecordForm({ ...recordForm, amount: e.target.value })} className="bg-zinc-800 border-zinc-700" data-testid="record-amount" />
                <p className="text-xs text-zinc-500 mt-1">Outstanding balance: ${activeInv.balance?.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-zinc-300">Reference (check #, ACH ID, wire ID)</Label>
                <Input value={recordForm.reference} onChange={(e) => setRecordForm({ ...recordForm, reference: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-300">Note</Label>
                <Input value={recordForm.note} onChange={(e) => setRecordForm({ ...recordForm, note: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-zinc-600 text-zinc-300" onClick={() => setRecordOpen(false)}>Cancel</Button>
            <Button className="font-bold" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} onClick={submitPayment} data-testid="record-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ContractMethodsRow({ contract, onSave }) {
  const initial = contract.allowed_payment_methods || ['ach', 'check', 'wire'];
  const [methods, setMethods] = useState(new Set(initial));

  const toggle = (m) => {
    const next = new Set(methods);
    if (next.has(m)) next.delete(m); else next.add(m);
    setMethods(next);
  };

  return (
    <div className="rounded-lg border border-white/10 p-3 bg-[#222] flex items-center justify-between gap-3 flex-wrap" data-testid={`contract-methods-${contract.contract_id}`}>
      <div className="min-w-0">
        <p className="text-white font-semibold truncate">{contract.property_name || contract.contract_id}</p>
        <p className="text-[11px] text-zinc-500">{contract.package_name || '—'}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {Object.entries(METHOD_LABELS).map(([key, label]) => (
          <button
            type="button"
            key={key}
            onClick={() => toggle(key)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${methods.has(key) ? 'bg-[#C9A962] border-[#C9A962] text-[#2D2D2D]' : 'bg-transparent border-zinc-600 text-zinc-300'}`}
            data-testid={`method-toggle-${contract.contract_id}-${key}`}
          >
            {label}
          </button>
        ))}
        <Button size="sm" className="font-bold ml-2" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} onClick={() => onSave(contract.contract_id, Array.from(methods))} data-testid={`save-methods-${contract.contract_id}`}>
          Save
        </Button>
      </div>
    </div>
  );
}
